/**
 * Location formatting shared by every venue-facing surface (RFP emails, chain-rep
 * outreach). Kept in one place so a city never reaches a hotel without its state.
 */

/**
 * Country values that mean "United States", normalized (trimmed + lowercased).
 *
 * Matching is exact against this set rather than a prefix test: `venue_static`
 * holds four spellings of the US ("United States", "UNITED STATES", "usa", and
 * one with a trailing space), while `U.S. Virgin Islands` (99 rows) would be
 * swept up by any `startsWith("u.s")` shortcut.
 */
const UNITED_STATES_NAMES = new Set(["united states", "united states of america", "usa", "us", "u.s.", "u.s.a."]);

/** Trimmed string, or "" for nullish and non-string input (jsonb is untyped at runtime). */
function cleanPart(value?: string | null): string {
  return typeof value === "string" ? value.trim() : "";
}

/**
 * Whether a country value denotes the United States.
 *
 * @param country - Country name in any of the spellings the data uses.
 * @returns True for a recognized US spelling; false for anything else, including blank.
 */
export function isUnitedStates(country?: string | null): boolean {
  return UNITED_STATES_NAMES.has(cleanPart(country).toLowerCase());
}

/**
 * Joins a city with its state/region for venue-facing copy — "Portland, Maine"
 * rather than a bare "Portland" (ENG-550: a hotel in Portland, ME had to email
 * back asking whether an RFP was meant for Portland, OR).
 *
 * Both sources of state store the FULL name, not the postal abbreviation:
 * `venue_static.state` ("Maine") and `events.formatted_locations[].state`
 * (Google Places `administrative_area_level_1` long text). So no abbreviation
 * mapping is needed, and the two agree in format.
 *
 * The city is qualified by whichever part actually disambiguates it:
 *
 * - **US venues** get the STATE — "Portland, Maine" vs "Portland, Oregon". It is
 *   guaranteed present for them (the `hotel_static_verify_city_state_united_states`
 *   trigger), and US same-name collisions are what ENG-550 was raised for.
 * - **Known non-US venues** get the COUNTRY — "Birmingham, United Kingdom", which
 *   is both the real collision abroad (vs Birmingham, Alabama) and what the event
 *   dashboard already shows. Their state is deliberately NOT used: ~77% carry one,
 *   but it is usually a city-state or an admin region that reads as noise to the
 *   hotel ("Birmingham, England", "Bangkok, Krung Thep Maha Nakhon").
 * - **Unknown country** (blank, or a caller with none to pass — ~2,380 venues)
 *   falls back to the state, so ENG-550 coverage is never silently lost.
 *
 * A qualifier that merely restates the city is dropped in every case: it cannot
 * disambiguate, and "Berlin, Berlin" / "Singapore, Singapore" read as mistakes.
 *
 * Parts are coerced rather than trusted: the `events.formatted_locations` caller
 * reads a jsonb column, so a non-string is a runtime possibility the type
 * signature can't rule out, and it must not throw on an email path.
 *
 * @param city - City name; blank, nullish, or non-string falls back to the qualifier alone.
 * @param state - State / province / region, used for US venues and when the country is unknown.
 * @param country - Venue country. Selects the qualifier, and becomes it outside the US.
 *   Omit only when the caller genuinely has no country available.
 * @returns "City, State" or "City, Country", whichever single part is present, or "" when neither is.
 */
export function formatCityState(city?: string | null, state?: string | null, country?: string | null): string {
  const cityPart = cleanPart(city);
  const statePart = cleanPart(state);
  const countryPart = cleanPart(country);

  // Outside the US the country disambiguates and the region does not; with no
  // country to go on, fall back to the state rather than qualifying nothing.
  const qualifier = countryPart && !isUnitedStates(countryPart) ? countryPart : statePart;

  if (!cityPart) return qualifier;
  // A qualifier that restates the city adds nothing — 12,922 venues in prod.
  if (!qualifier || qualifier.toLowerCase() === cityPart.toLowerCase()) return cityPart;

  return `${cityPart}, ${qualifier}`;
}

/** One considered location, as carried by `events.formatted_locations`. */
export interface CityStateLocation {
  city?: string | null;
  state?: string | null;
  country?: string | null;
}

/**
 * Formats several considered locations into one venue-facing string. Entries are
 * separated by "; " because each one may itself contain a comma ("Portland,
 * Maine; Austin, Texas") — a comma-joined list would read as four places.
 *
 * @param locations - Locations to format; entries with no city and no state are dropped.
 * @returns The joined list, or "" when nothing is formattable.
 */
export function formatCityStateList(locations: Array<CityStateLocation | null | undefined>): string {
  return locations
    .map((loc) => formatCityState(loc?.city, loc?.state, loc?.country))
    .filter(Boolean)
    .join("; ");
}

/**
 * The venue-facing "cities the client is considering" string for an event —
 * the structured `formatted_locations` when present, else the flat
 * `events.location` column.
 *
 * Single source for both RFP builders (the current `buildRepRfpHtml` and the
 * legacy `createTableRows`), which previously derived this independently and had
 * already drifted on whether the fallback was trimmed.
 *
 * The fallback can still be state-less: only the create-event wizard writes
 * `events.location` in city/state form, and pre-ENG-550 rows plus the northstar
 * draft-event path hold bare city names.
 *
 * @param event - Event carrying `formatted_locations` and/or `location`.
 * @returns The formatted list, the trimmed fallback, or "" when neither is set.
 */
export function formatEventCitiesConsidered(event: {
  formatted_locations?: Array<CityStateLocation | null | undefined> | null;
  location?: string | null;
}): string {
  return formatEventCitiesConsideredEntries(event).join("; ");
}

/**
 * The same considered locations as {@link formatEventCitiesConsidered}, but one
 * entry per element so a caller can render them as a list rather than a single
 * run-on line ("Birmingham, United Kingdom; Swindon, United Kingdom; …").
 *
 * The `events.location` fallback is a single opaque string — it is returned as
 * one entry rather than split, because its separator differs by vintage (", "
 * before ENG-550, "; " after) and a city/state entry contains its own comma.
 *
 * @param event - Event carrying `formatted_locations` and/or `location`.
 * @returns One formatted location per entry; empty when the event names none.
 */
export function formatEventCitiesConsideredEntries(event: {
  formatted_locations?: Array<CityStateLocation | null | undefined> | null;
  location?: string | null;
}): string[] {
  const entries = (event.formatted_locations ?? [])
    .map((loc) => formatCityState(loc?.city, loc?.state, loc?.country))
    .filter(Boolean);
  if (entries.length > 0) return entries;

  const fallback = event.location?.trim() || "";
  return fallback ? [fallback] : [];
}
